import React, {useEffect, useState} from 'react';
import {
  Text,
  TouchableOpacity,
  View,
  StyleSheet,
  Dimensions
} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import I18n from 'i18n-js';
import {useDispatch, useSelector} from 'react-redux';
import {
  setCartAssignmentDetailsPrompt,
  setCartAssignmentToast,
  setPreviousScanInfo,
  setRfoNoOrders,
  setCartToastMessage
} from '../../actions/scanToCartActions';

const AssignmentToast = ({onRunImport = {}, topOffset = 0}) => {
  const dispatch = useDispatch();
  const [windowDimensions, setWindowDimensions] = useState(
    Dimensions.get('window')
  );

  const {cartAssignmentDetails, notAssignmentOrderDetails, activeCart, previousScanInfo, isRfoNoOrders, cartToastMessage, cartToastOptions} =
    useSelector(state => state.scanToCart);

  const {notAssignedOrders = [], assignedOrders = []} =
    cartAssignmentDetails || {};

  // Get counts
  const failedCount = notAssignedOrders.length;
  const successCount = assignedOrders.length;
  const cartId = activeCart?.cart_name || '';

  // Determine toast type
  const getToastType = () => {
    if (cartToastMessage) {
      return 'genericMessage';
    }
    if (isRfoNoOrders) {
      return 'rfoNoOrders';
    }
    if (previousScanInfo) {
      return 'previouslyScanned';
    }
    if (
      notAssignedOrders.length === 0 &&
      assignedOrders.length === 0 &&
      notAssignmentOrderDetails !== null
    ) {
      return 'noOrders';
    } else if (failedCount > 0 && successCount === 0) {
      return 'failed';
    } else if (failedCount > 0 && successCount > 0) {
      return 'mixed';
    } else if (successCount > 0 && failedCount === 0) {
      return 'success';
    }
    return 'success';
  };

  const toastType = getToastType();

  const clearPreviousScanInfo = () => {
    if (toastType === 'previouslyScanned') {
      dispatch(setPreviousScanInfo(null));
    }
  };

  const isStickyToast = toastType === 'genericMessage' && cartToastOptions?.sticky === true;

  useEffect(() => {
    if (toastType === 'noOrders' || isStickyToast) {
      return;
    }

    const timer = setTimeout(() => {
      dispatch(setCartAssignmentToast(false));
      if (toastType === 'rfoNoOrders') {
        dispatch(setRfoNoOrders(false));
      }
      if (toastType === 'genericMessage') {
        dispatch(setCartToastMessage(null));
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [dispatch, toastType, isStickyToast]);

  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({window}) => {
      setWindowDimensions(window);
    });

    return () => subscription?.remove();
  }, []);

  const {width: windowWidth, height: windowHeight} = windowDimensions;

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      const options = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      };
      return date.toLocaleString('en-US', options);
    } catch (e) {
      return timestamp;
    }
  };

  // Determine toast configuration based on type
  const getToastConfig = () => {
    if (toastType === 'genericMessage') {
      return {
        colors: cartToastOptions?.severity === 'error' ? ['rgb(207,68,55)', 'rgb(122,40,40)'] : ['#4A90E2', '#2E5BBA'],
        message: cartToastMessage,
        showViewDetails: false,
        showRunImport: false,
        isMultiLine: true
      };
    }
    if (toastType === 'rfoNoOrders') {
      return {
        colors: ['rgb(207,68,55)', 'rgb(122,40,40)'],
        message: I18n.t('cartAssignment.rfoNoOrders'),
        showViewDetails: false,
        showRunImport: true,
        isMultiLine: false
      };
    } else if (toastType === 'previouslyScanned') {
      const formattedTimestamp = formatTimestamp(previousScanInfo?.timestamp);
      return {
        colors: ['#4A90E2', '#2E5BBA'],
        message: I18n.t('cartAssignment.previouslyScanned'),
        details: I18n.t('cartAssignment.previouslyScannedDetails', {
          username: previousScanInfo?.username || '',
          timestamp: formattedTimestamp
        }),
        showViewDetails: false,
        showRunImport: false,
        isMultiLine: true
      };
    } else if (toastType === 'noOrders') {
      return {
        colors: ['rgb(207,68,55)', 'rgb(122,40,40)'],
        message: I18n.t('cartAssignment.noOrders'),
        showViewDetails: false,
        showRunImport: true,
        isMultiLine: true
      };
    } else if (toastType === 'mixed') {
      return {
        colors: ['rgb(207,68,55)', 'rgb(122,40,40)'],
        message: I18n.t('cartAssignment.mixedResults', {
          failedCount,
          successCount,
          cartId
        }),
        showViewDetails: true,
        showRunImport: false,
        isMultiLine: false
      };
    } else if (toastType === 'failed') {
      return {
        colors: ['rgb(207,68,55)', 'rgb(122,40,40)'],
        message: I18n.t('cartAssignment.failedOnly', {
          failedCount,
          cartId
        }),
        showViewDetails: true,
        showRunImport: false,
        isMultiLine: false
      };
    } else {
      return {
        colors: ['#4A90E2', '#2E5BBA'],
        message: I18n.t('cartAssignment.successOnly', {
          successCount,
          cartId
        }),
        showViewDetails: true,
        showRunImport: false,
        isMultiLine: false
      };
    }
  };

  const config = getToastConfig();

  const handleClose = () => {
    dispatch(setCartAssignmentToast(false));
    clearPreviousScanInfo();
    if (toastType === 'rfoNoOrders') {
      dispatch(setRfoNoOrders(false));
    }
    if (toastType === 'genericMessage') {
      dispatch(setCartToastMessage(null));
    }
  };

  const handleRunImport = () => {
    dispatch(setCartAssignmentToast(false));
    if (onRunImport) {
      onRunImport();
    }
  };

  const handleViewDetails = () => {
    
    dispatch(setCartAssignmentToast(false));
    dispatch(setCartAssignmentDetailsPrompt(true));
  };

  // Render the detailed breakdown for no orders case
  const renderNoOrdersContent = () => {
    const { awaiting_recent_orders_count,
      awaiting_window_days,
      orders_assigned_to_other_users_count,
      orders_available_for_this_cart,
      orders_tagged_for_other_carts_count,
      orders_excluded_by_zone_access_count,
      empty_totes_available
 } = notAssignmentOrderDetails.data.order_debug_info;
    return (
      <View style={styles.noOrdersContainer}>
        <Text style={styles.noOrdersTitle}>
          {I18n.t('cartAssignment.noOrders')}{' '}
          <Text style={styles.clickHereText} onPress={handleRunImport}>
            {I18n.t('cartAssignment.clickHere')}
          </Text>{' '}
          {I18n.t('cartAssignment.toRunImport')}
        </Text>

        <View style={styles.breakdownContainer}>
          <Text style={styles.breakdownText}>
            {I18n.t('cartAssignment.awaitingOrders', {days: awaiting_window_days, count: awaiting_recent_orders_count})}{'\n'}
            {I18n.t('cartAssignment.assignedToOthers', {count:orders_assigned_to_other_users_count})}{'\n'}
            {I18n.t('cartAssignment.taggedForOtherCart', {count: orders_tagged_for_other_carts_count})}{'\n'}
            {I18n.t('cartAssignment.availableForCart', {count: orders_available_for_this_cart})}
            {orders_excluded_by_zone_access_count > 0 ? '\n' + I18n.t('cartAssignment.excludedByZone', {count: orders_excluded_by_zone_access_count}) : ''}
            {empty_totes_available > 0 ? '\n' + I18n.t('cartAssignment.emptyTotes', {count: empty_totes_available}) : ''}
          </Text>
        </View>
      </View>
    );
  };

  return (
    <View style={[styles.toastContainer, {top: topOffset}]}>
      <LinearGradient
        testID={
          toastType === 'rfoNoOrders'
            ? 'rfoNoOrdersToast'
            : toastType === 'noOrders'
            ? 'noOrdersToast'
            : toastType === 'mixed'
            ? 'mixedAssignmentToast'
            : toastType === 'previouslyScanned'
            ? 'previouslyScannedToast'
            : 'successAssignmentToast'
        }
        locations={[0, 1]}
        colors={config.colors}
        style={[
          styles.toastGradient,
          {
            minHeight: toastType === 'previouslyScanned' ? 85 : config.isMultiLine ? 120 : 50,
            paddingVertical: toastType === 'previouslyScanned' ? 8 : 12
          }
        ]}>
        <View style={styles.contentContainer}>
          {toastType === 'rfoNoOrders' ? (
            <Text style={styles.messageText}>
              {config.message}{' '}
              <Text style={styles.clickHereText} onPress={handleRunImport}>
                {I18n.t('cartAssignment.clickHere')}
              </Text>
              {' '}{I18n.t('cartAssignment.toRunImport')}
            </Text>
          ) : toastType === 'noOrders' ? (
            renderNoOrdersContent()
          ) : toastType === 'previouslyScanned' ? (
            <View style={styles.previouslyScannedContainer}>
              <Text style={styles.messageText}>
                {config.message}
              </Text>
              {config.details && (
                <Text style={styles.detailsText}>
                  {config.details}
                </Text>
              )}
            </View>
          ) : (
            <Text style={styles.messageText}>
              {config.message}
              {config.showViewDetails && (
                <Text
                  style={styles.viewDetailsText}
                  onPress={handleViewDetails}>
                  {' '}
                  {I18n.t('cartAssignment.viewDetails')}
                </Text>
              )}
            </Text>
          )}
        </View>

        <View style={styles.closeButtonRow}>
          <TouchableOpacity
            style={styles.closeButton}
            testID="closeToastButton"
            onPress={handleClose}>
            <Text style={styles.closeButtonText}>{I18n.t('cartAssignment.closeToast')}</Text>
          </TouchableOpacity>
        </View>
      </LinearGradient>
    </View>
  );
};

const styles = StyleSheet.create({
  toastContainer: {
    position: 'absolute',
    left: 0,
    right: 0,
    zIndex: 1000,
    alignItems: 'center',
    paddingHorizontal: 15
  },
  toastGradient: {
    borderRadius: 8,
    minHeight: 50,
    flexDirection: 'column',
    alignItems: 'stretch',
    paddingVertical: 12,
    paddingHorizontal: 15,
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    width: '80%',
    maxWidth: 600
  },
  contentContainer: {
    flex: 1
  },
  messageText: {
    color: '#fff',
    fontFamily: 'Poppins_300Light',
    lineHeight: 22,
    fontWeight: '400',
    textAlign: 'center'
  },
  viewDetailsText: {
    color: '#fff',
    fontFamily: 'Poppins_300Light',
    textDecorationLine: 'underline',
    fontWeight: '500'
  },
  closeButtonRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 10
  },
  closeButton: {
    minHeight: 36,
    paddingVertical: 6,
    paddingHorizontal: 22,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.85)',
    backgroundColor: 'rgba(255,255,255,0.12)',
    justifyContent: 'center',
    alignItems: 'center'
  },
  closeButtonText: {
    fontSize: 14,
    color: '#fff',
    fontFamily: 'Poppins_400Regular'
  },
  noOrdersContainer: {
    flex: 1,
    alignItems: 'center'
  },
  noOrdersTitle: {
    fontSize: 16,
    color: '#fff',
    fontFamily: 'Poppins_300Light',
    fontWeight: '400',
    textAlign: 'center',
    marginBottom: 10
  },
  clickHereText: {
    fontSize: 16,
    color: '#fff',
    fontFamily: 'Poppins_300Light',
    textDecorationLine: 'underline',
    fontWeight: '500'
  },
  breakdownContainer: {
    width: '100%',
    alignItems: 'center'
  },
  breakdownText: {
    fontSize: 14,
    color: '#fff',
    fontFamily: 'Poppins_300Light',
    fontWeight: '300',
    textAlign: 'center',
    marginBottom: 2,
    lineHeight: 18
  },
  previouslyScannedContainer: {
    flex: 1,
    alignItems: 'center'
  },
  detailsText: {
    fontSize: 13,
    color: '#fff',
    fontFamily: 'Poppins_300Light',
    fontWeight: '300',
    textAlign: 'center',
    marginTop: 3,
    lineHeight: 18
  }
});

export default AssignmentToast;
